import React, { useRef } from 'react';
import {
  Alert,
  Box,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import ComboBox, { DefaultDataType as ComboboxDataType } from '../../../layout/ComboBox';
import { ExtendedPayload, ExtendedReview } from '../../../../redux/types';

export type CommentSelectorProps = {
  data?: ExtendedReviewSection[];
  sectionHeading?: string | React.ReactNode;
  onSelect: (arg1: MultiCommentSection) => void;
  selectedValues? : MultiCommentSection;
};

type ExtendedReviewSection = ExtendedReview['data']['section'][0];
type MultiCommentSection = ExtendedPayload['sections'][0];
type MultiComment = MultiCommentSection['arg']['comments'][0];
type ComboCommentType = MultiComment & ComboboxDataType;
type KeyToHeadlineMap = {
  [key: string]: string;
}

const CommentSelector: React.FC<CommentSelectorProps> = ({
  sectionHeading,
  data,
  onSelect,
  selectedValues = {},
}: CommentSelectorProps) => {
  const keyToHeadline = useRef<KeyToHeadlineMap>({});

  const transformData = (commentData: ExtendedReviewSection): ComboCommentType[] => Object
    .entries(commentData.options)
    .map((el) => {
      keyToHeadline.current[commentData.key] = commentData.text;
      return {
        value: el[0],
        label: `${el[0]}. ${el[1]}`,
        key: commentData.key,
      };
    });

  const getUpdatedMultiSection = (
    commentList: MultiComment[],
    key: string,
  ): MultiCommentSection => {
    const headline = keyToHeadline.current[key];
    const newSelectedValues: MultiCommentSection = { ...selectedValues };
    newSelectedValues[key] = {
      headline,
      comments: commentList,
    };
    return newSelectedValues;
  };

  const onCommentSelect = (commentList: ComboCommentType[] | null, keyId: string) => {
    const selComments: MultiComment[] = commentList || [];
    const multiSection = getUpdatedMultiSection(selComments, keyId);
    onSelect(multiSection);
  };

  return (
    <Paper sx={{ p: '10px' }} variant="outlined">
      <Box sx={{ my: 1 }}><Typography variant="h5">{sectionHeading}</Typography></Box>
      {
        !data && (
          <Alert severity="info">
            No Textblock comments are available for this section.
          </Alert>
        )
      }
      <Box>
        {
          data?.map((commentData) => (
            <Grid sx={{ my: 0 }} container spacing={2} key={commentData.text}>
              <Grid item sm={3} xs={12}>
                <Typography variant="body1">{commentData.text}</Typography>
              </Grid>
              <Grid item sm={9} xs={12}>
                <ComboBox<ComboCommentType>
                  data={transformData(commentData)}
                  keyId={commentData.key}
                  onChange={onCommentSelect}
                  isMulti
                  selectedItems={selectedValues[commentData.key]?.comments}
                />
              </Grid>
            </Grid>
          ))
        }
      </Box>
    </Paper>
  );
};

export default CommentSelector;
